/**
 * Structured Digital Database — SEBI (Prohibition of Insider Trading)
 * Regulations, 2015, Reg 3(5)/3(6).
 *
 * Four tabs over one company's register: the sharing log itself, the people who
 * may give or receive UPSI, the information being protected, and an integrity
 * check.
 *
 * The most important thing about this screen is what it does NOT contain: the
 * register has no Edit and no Delete control anywhere. That is not an oversight
 * and not a permission the UI hides — the API has no such endpoint, and the
 * database rejects the mutation beneath it. A mistaken row is superseded by a
 * correction, and both stay visible. Please do not "helpfully" add an edit
 * button here; it would fail three layers down.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Chip from '../components/common/Chip';
import Modal from '../components/common/Modal';
import { Field, TextField, Select, DateField, labelClass, inputClass, textareaClass } from '../components/common/FormControls';
import {
  TbShieldCheck, TbShieldX, TbPlus, TbUsers, TbFileText, TbBook2, TbDownload, TbAlertTriangle,
  TbLayoutDashboard, TbClockExclamation, TbMail, TbInbox,
} from 'react-icons/tb';
import { useApp } from '../context/AppContext';
import { useUI } from '../context/UIContext';
import { sddAPI } from '../services/api';
import { isListedCompany, SDD_LISTING_NOTE } from '../utils/companyListing';

/* ─── Vocabulary, mirrored from the models ─────────────────── */

const INSIDER_CATEGORIES = [
  { value: 'director', label: 'Director' },
  { value: 'kmp', label: 'KMP' },
  { value: 'promoter', label: 'Promoter' },
  { value: 'designated_person', label: 'Designated Person' },
  { value: 'employee', label: 'Employee' },
  { value: 'immediate_relative', label: 'Immediate Relative' },
  { value: 'external_auditor', label: 'Auditor (external)' },
  { value: 'external_legal', label: 'Legal counsel (external)' },
  { value: 'external_banker', label: 'Banker (external)' },
  { value: 'external_valuer', label: 'Valuer (external)' },
  { value: 'external_consultant', label: 'Consultant (external)' },
  { value: 'external_other', label: 'Other (external)' },
];

const UPSI_CATEGORIES = [
  { value: 'financial_results', label: 'Financial results' },
  { value: 'dividend', label: 'Dividend' },
  { value: 'change_in_capital_structure', label: 'Change in capital structure' },
  { value: 'mergers_acquisitions_delisting', label: 'Merger / acquisition / delisting' },
  { value: 'disposal_expansion', label: 'Disposal or expansion' },
  { value: 'change_in_kmp', label: 'Change in KMP' },
  { value: 'material_contract_or_order', label: 'Material contract or order' },
  { value: 'rating_change', label: 'Rating change' },
  { value: 'litigation_or_regulatory', label: 'Litigation or regulatory action' },
  { value: 'other', label: 'Other' },
];

const SHARE_MODES = [
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'board_meeting', label: 'Board meeting' },
  { value: 'call', label: 'Call' },
  { value: 'video_call', label: 'Video call' },
  { value: 'physical_document', label: 'Physical document' },
  { value: 'data_room', label: 'Data room' },
  { value: 'messaging', label: 'Messaging' },
  // Handed over inside Compliance Saarthi — what a suggestion raised from a
  // document share carries.
  { value: 'document_share', label: 'Shared in Compliance Saarthi' },
  { value: 'other', label: 'Other' },
];

const TABS = [
  { id: 'overview', label: 'Overview', icon: TbLayoutDashboard },
  { id: 'register', label: 'Register', icon: TbBook2 },
  { id: 'pending', label: 'Needs attention', icon: TbInbox },
  { id: 'insiders', label: 'Insiders', icon: TbUsers },
  { id: 'events', label: 'UPSI Events', icon: TbFileText },
  { id: 'notices', label: 'Notices', icon: TbMail },
  { id: 'integrity', label: 'Integrity', icon: TbShieldCheck },
];

// A sharing recorded more than this long after it happened is flagged on the
// overview. Same threshold the record endpoint warns on, so the two agree.
const LATE_ENTRY_DAYS = 7;

/* ─── Helpers ──────────────────────────────────────────────── */

const labelOf = (options, value) => options.find((o) => o.value === value)?.label || value || '—';

const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} ${d
    .toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

const errText = (error) => error?.message || 'Something went wrong. Please try again.';

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-[#e8e2d6] p-5 ${className}`}>{children}</div>
);

const Empty = ({ children }) => (
  <div className="text-center py-10 text-[13px] text-[#7a7060]">{children}</div>
);

/* ═══════════════════════════════════════════════════════════ */

export default function SddPage() {
  const { activeCompany } = useApp();
  const { showToast } = useUI();

  const companyId = activeCompany?._id || activeCompany?.id || null;

  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [insiders, setInsiders] = useState([]);
  const [events, setEvents] = useState([]);
  const [rows, setRows] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [retention, setRetention] = useState(null);
  const [access, setAccess] = useState(null);
  const [exportFormat, setExportFormat] = useState(null);
  const [includePan, setIncludePan] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Default to no capabilities until the server returns this user's SDD grant.
  const can = access?.can || {};

  /* ── Loading ─────────────────────────────────────────────── */

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Authority first: a reviewer must not be shown controls the server will
      // refuse, and someone with no register access should see nothing at all.
      const accessRes = await sddAPI.getMyAccess(companyId);
      const myAccess = accessRes?.data || null;
      setAccess(myAccess);
      if (!myAccess?.can?.viewRegister) {
        setInsiders([]); setEvents([]); setRows([]); setRetention(null);
        return;
      }

      const [insiderRes, eventRes, registerRes, retentionRes] = await Promise.all([
        sddAPI.getInsiders(companyId),
        sddAPI.getEvents(companyId),
        sddAPI.getRegister(companyId, { limit: 200 }),
        sddAPI.getRetention(companyId),
      ]);
      setInsiders(insiderRes?.data?.insiders || []);
      setEvents(eventRes?.data?.events || []);
      setRows(registerRes?.data || []);
      setRetention(retentionRes?.data || null);
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, showToast]);

  useEffect(() => {
    loadAll();
    setIntegrity(null);
  }, [loadAll]);

  /* ── Actions ─────────────────────────────────────────────── */

  const runVerify = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await sddAPI.verify(companyId);
      setIntegrity(res?.data || null);
      setTab('integrity');
      showToast(res?.data?.ok ? 'Register verified — no alterations.' : 'Integrity failures found.', res?.data?.ok ? 'success' : 'error');
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, showToast]);

  const openExport = useCallback((format) => {
    setExportFormat(format);
    setIncludePan(false);
  }, []);

  const runExport = useCallback(async () => {
    if (!companyId || !exportFormat) return;
    setExporting(true);
    try {
      // Masked identifiers by default. Revealing full PANs is a separate, audited
      // action — not something a general Export button should do silently.
      const { blob, filename } = await sddAPI.downloadRegister(companyId, {
        format: exportFormat,
        includePan: can.exportFullPan && includePan,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      setExportFormat(null);
      showToast('Register exported, with its integrity result attached.', 'success');
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setExporting(false);
    }
  }, [can.exportFullPan, companyId, exportFormat, includePan, showToast]);

  const placeHold = useCallback(async (payload) => {
    if (!companyId) return;
    try {
      const res = await sddAPI.placeLegalHold(companyId, payload);
      showToast(res?.message || 'Legal hold placed.', 'success');
      loadAll();
    } catch (error) {
      showToast(errText(error), 'error');
    }
  }, [companyId, loadAll, showToast]);

  const releaseHold = useCallback(async (holdId, reason) => {
    if (!companyId) return;
    try {
      await sddAPI.releaseLegalHold(companyId, holdId, reason);
      showToast('Legal hold released.', 'success');
      loadAll();
    } catch (error) {
      showToast(errText(error), 'error');
    }
  }, [companyId, loadAll, showToast]);

  /* ── Derived ─────────────────────────────────────────────── */

  const listed = isListedCompany(activeCompany);
  // Server-supplied capability map. Defaults to nothing until it arrives, so no
  // write control ever flashes on screen before authority is known.
  const activeInsiders = useMemo(() => insiders.filter((i) => !i.effectiveTo), [insiders]);
  const unpublishedEvents = useMemo(() => events.filter((e) => e.status !== 'published'), [events]);
  const supersededBy = useMemo(() => {
    const map = new Map();
    for (const row of rows) if (row.correctionOf) map.set(String(row.correctionOf), row.seq);
    return map;
  }, [rows]);

  /* ── No company selected ─────────────────────────────────── */

  if (!companyId) {
    return (
      <AppLayout>
        <PageHeader
          title="UPSI Register"
          subtitle="Structured Digital Database under SEBI (PIT) Regulations, 2015, Reg 3(5)"
        />
        <Card>
          <Empty>Select a company from the sidebar to open its register.</Empty>
        </Card>
      </AppLayout>
    );
  }

  /* ── No register access ──────────────────────────────────── */
  if (access && !can.viewRegister) {
    return (
      <AppLayout>
        <PageHeader title="UPSI Register" subtitle={activeCompany?.name || 'Company'} />
        <Card>
          <Empty>
            You do not have access to this company’s UPSI register.
            <div className="mt-2">
              Its Compliance Officer can grant you Reviewer or Contributor access.
            </div>
          </Empty>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title="UPSI Register"
        subtitle={`${activeCompany?.name || 'Company'} · Who received unpublished price sensitive information, and why · Structured Digital Database under SEBI (PIT) Regulations, 2015, Reg 3(5)`}
        actions={
          <>
            {access && (
              <Chip tone={can.manageAccess ? '#4a6b3f' : '#8a6a1f'}>
                {access.label}{access.inherited ? ' (default)' : ''}
              </Chip>
            )}
            {can.exportMasked && (
              <>
                <Button variant="outline" size="sm" leftIcon={<TbDownload />} onClick={() => openExport('xlsx')}>
                  Excel
                </Button>
                <Button variant="outline" size="sm" leftIcon={<TbDownload />} onClick={() => openExport('pdf')}>
                  PDF
                </Button>
              </>
            )}
            {can.verify && (
              <Button variant="ink" size="sm" leftIcon={<TbShieldCheck />} onClick={runVerify} loading={loading}>
                Verify integrity
              </Button>
            )}
          </>
        }
      />

      <Modal
        open={!!exportFormat}
        onClose={() => !exporting && setExportFormat(null)}
        title={`Export ${exportFormat === 'xlsx' ? 'Excel' : 'PDF'} register`}
        bodyClassName="p-6"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setExportFormat(null)} disabled={exporting}>Cancel</Button>
            <Button leftIcon={<TbDownload />} onClick={runExport} loading={exporting}>Export</Button>
          </>
        )}
      >
        <p className="text-[13px] text-[#5f574b] leading-relaxed mb-4">
          Identifiers are masked by default. The export also includes the register integrity result.
        </p>
        {can.exportFullPan && (
          <label className="flex items-start gap-3 rounded-xl border border-[#e2d9c8] bg-[#faf9f6] p-4 cursor-pointer">
            <input
              type="checkbox"
              checked={includePan}
              onChange={(event) => setIncludePan(event.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#145f5f]"
            />
            <span>
              <span className="block text-[13px] font-semibold text-[#17130d]">Include full PANs / identifiers</span>
              <span className="block mt-1 text-[11px] leading-relaxed text-[#7a7060]">
                This reveals sensitive personal data. The disclosure is recorded in the audit log.
              </span>
            </span>
          </label>
        )}
      </Modal>

      {/* Advisory, never a gate. Availability is not a legal opinion, and the
          duty attaches to intermediaries, fiduciaries and IPO-bound companies
          that are all unlisted at the time. */}
      {!listed && (
        <div className="flex items-start gap-2 mb-5 p-3 rounded-lg bg-[#f3ebd7] text-[#8a6a1f] text-[12px] leading-relaxed">
          <TbAlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>{SDD_LISTING_NOTE}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-[#e8e2d6] flex-wrap">
        {TABS.filter((t) => !t.needs || can[t.needs]).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-[13px] font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition ${
              tab === id
                ? 'border-[#8a6a1f] text-[#8a6a1f]'
                : 'border-transparent text-[#7a7060] hover:text-[#4a4034]'
            }`}
          >
            <Icon size={16} />
            {label}
            {id === 'register' && rows.length > 0 && (
              <span className="text-[11px] text-[#a09684]">({rows.length})</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <OverviewTab
          rows={rows}
          insiders={insiders}
          events={events}
          retention={retention}
          integrity={integrity}
          can={can}
          onVerify={runVerify}
          onOpenRegister={() => setTab('register')}
        />
      )}

      {tab === 'notices' && (
        <NoticesTab companyId={companyId} can={can} showToast={showToast} />
      )}

      {tab === 'pending' && (
        <PendingTab
          companyId={companyId}
          events={events}
          can={can}
          onConfirmed={loadAll}
          showToast={showToast}
        />
      )}

      {tab === 'register' && (
        <RegisterTab
          companyId={companyId}
          rows={rows}
          insiders={activeInsiders}
          events={events}
          supersededBy={supersededBy}
          can={can}
          onChanged={loadAll}
          showToast={showToast}
        />
      )}

      {tab === 'insiders' && (
        <InsidersTab companyId={companyId} insiders={insiders} can={can} onChanged={loadAll} showToast={showToast} />
      )}

      {tab === 'events' && (
        <EventsTab companyId={companyId} events={events} can={can} onChanged={loadAll} showToast={showToast} />
      )}

      {tab === 'integrity' && (
        <IntegrityTab
          integrity={integrity}
          retention={retention}
          rowCount={rows.length}
          can={can}
          onVerify={runVerify}
          onPlaceHold={placeHold}
          onReleaseHold={releaseHold}
          loading={loading}
        />
      )}

      {loading && tab !== 'integrity' && (
        <div className="text-[12px] text-[#7a7060] mt-3">Loading…</div>
      )}

      <p className="text-[11px] text-[#a09684] mt-6 leading-relaxed">
        Rows in this register cannot be edited or deleted — by law and by construction. To fix a
        mistake, record a correction: the original stays visible and the correction is linked to it.
        {unpublishedEvents.length > 0 && ` ${unpublishedEvents.length} UPSI event(s) are still unpublished.`}
      </p>
    </AppLayout>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  OVERVIEW                                                  */
/* ═══════════════════════════════════════════════════════════ */

/**
 * Computed entirely from data the page has already loaded — no extra endpoint,
 * no second round trip, and nothing here can disagree with the tabs beside it.
 *
 * Scope is deliberately the SDD only. Trading requests, pre-clearance and the
 * trading-window calendar belong to the wider PIT module; putting empty tiles
 * for them here would promise a feature that does not exist.
 */
function OverviewTab({ rows, insiders, events, retention, integrity, can, onVerify, onOpenRegister }) {
  const stats = useMemo(() => {
    const activeInsiders = insiders.filter((i) => !i.effectiveTo).length;
    const unpublished = events.filter((e) => e.status !== 'published').length;
    const lateMs = LATE_ENTRY_DAYS * 24 * 60 * 60 * 1000;
    const late = rows.filter(
      (r) => new Date(r.createdAt) - new Date(r.sharedAt) > lateMs,
    ).length;
    const corrections = rows.filter((r) => r.correctionOf).length;
    const latest = rows.reduce(
      (acc, r) => (!acc || new Date(r.sharedAt) > new Date(acc.sharedAt) ? r : acc),
      null,
    );
    return { activeInsiders, unpublished, late, corrections, latest };
  }, [rows, insiders, events]);

  const tiles = [
    { label: 'Register entries', value: rows.length },
    { label: 'UPSI events', value: events.length, hint: `${stats.unpublished} still unpublished` },
    { label: 'Active insiders', value: stats.activeInsiders, hint: `${insiders.length} on the list` },
    { label: 'Corrections', value: stats.corrections },
  ];

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {tiles.map((tile) => (
          // Body font, not the serif: Cormorant sets old-style figures, so a
          // count of 1 renders as an I and 0 as an O. tabular-nums keeps the
          // tiles from shifting width as the numbers change.
          <Card key={tile.label} className="!p-4">
            <div className="text-[26px] font-bold leading-none tabular-nums">
              {tile.value}
            </div>
            <div className="text-[12px] text-[#7a7060] mt-1.5">{tile.label}</div>
            {tile.hint && <div className="text-[11px] text-[#a09684] mt-0.5">{tile.hint}</div>}
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <div className="text-[13px] font-bold mb-2">Integrity</div>
          {integrity ? (
            <div className={`text-[13px] ${integrity.ok ? 'text-[#4a6b3f]' : 'text-[#9a564a]'}`}>
              {integrity.ok ? '✓ Register intact' : '✗ Register has been altered'}
              <div className="text-[12px] text-[#7a7060] mt-0.5">{integrity.message}</div>
            </div>
          ) : (
            <div className="text-[12px] text-[#7a7060]">
              Not verified in this session.
              {can.verify && (
                <Button variant="ghost" size="xs" className="ml-1" onClick={onVerify}>Verify now</Button>
              )}
            </div>
          )}
        </Card>

        <Card>
          <div className="text-[13px] font-bold mb-2">Retention</div>
          {retention ? (
            <>
              <div className="text-[12px] text-[#7a7060]">{retention.summary}</div>
              {retention.heldByLaw && (
                <Chip tone="#9a564a" className="mt-2">Legal hold active</Chip>
              )}
            </>
          ) : (
            <div className="text-[12px] text-[#7a7060]">—</div>
          )}
        </Card>
      </div>

      {stats.late > 0 && (
        <Card className="mt-3 border-[#8a6a1f]">
          <div className="flex items-start gap-2 text-[12px] text-[#8a6a1f]">
            <TbClockExclamation size={16} className="shrink-0 mt-0.5" />
            <span>
              {stats.late} {stats.late === 1 ? 'entry was' : 'entries were'} recorded more than{' '}
              {LATE_ENTRY_DAYS} days after the sharing happened. The delay is visible in the
              register and to anyone inspecting it.
            </span>
          </div>
        </Card>
      )}

      <Card className="mt-3">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[13px] font-bold">Recent sharings</div>
          <Button variant="ghost" size="xs" onClick={onOpenRegister}>Open register</Button>
        </div>
        {rows.length === 0 ? (
          <Empty>Nothing recorded yet.</Empty>
        ) : (
          <div className="space-y-2.5">
            {rows.slice(0, 5).map((row) => (
              <div key={row._id} className="text-[13px] flex gap-3 flex-wrap">
                <span className="text-[#a09684] font-mono">#{row.seq}</span>
                <span className="font-semibold">{row.natureOfUpsi}</span>
                <span className="text-[#7a7060]">
                  → {(row.recipients || []).map((r) => r.name).join(', ')}
                </span>
                <span className="text-[#a09684] ml-auto whitespace-nowrap">{fmtDateTime(row.sharedAt)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  PENDING ENTRIES                                           */
/* ═══════════════════════════════════════════════════════════ */

const SOURCE_LABELS = {
  draft_shared: 'Draft shared',
  share_link: 'Share link created',
  document_uploaded: 'Document uploaded',
  meeting_minutes: 'Meeting minutes approved',
  manual: 'Raised by hand',
  email: 'Email copied to the UPSI address',
};

/** Stable handle for an observed person who may not be an insider yet. */
const keyOf = (recipient) => String(recipient.email || recipient.name || '').toLowerCase();

/**
 * Document types are stored as slugs. "board_meeting" on screen reads as a
 * database field that escaped, and this is the queue where someone decides what
 * a sharing was.
 */
const docTypeLabel = (type) => String(type || '')
  .replace(/[_-]+/g, ' ')
  .replace(/^\w/, (c) => c.toUpperCase());

/**
 * How the information travelled, named as specifically as the record allows.
 *
 * "Draft shared" answers the channel but not the question a compliance officer
 * is actually asking — a resolution and a set of board minutes are different
 * things to have handed over, and the document type is already on the
 * suggestion. Falls back to the plain channel label when the type is unknown,
 * which is the case for suggestions raised before that field existed.
 */
const howShared = (proposal) => {
  const channel = SOURCE_LABELS[proposal.source] || proposal.source;
  if (proposal.source === 'email') {
    // The attachment names are in sourceDocumentRef; count them so the officer
    // sees at a glance whether a document travelled or only a message.
    const files = String(proposal.sourceDocumentRef || '').split(';').map((s) => s.trim()).filter(Boolean);
    return files.length
      ? `Email with ${files.length} attachment${files.length === 1 ? '' : 's'}, copied to the UPSI address`
      : 'Email with no attachment, copied to the UPSI address';
  }
  if (proposal.source !== 'draft_shared' || !proposal.sourceDocumentType) return channel;
  return `${docTypeLabel(proposal.sourceDocumentType)} shared inside Compliance Saarthi`;
};

/* ═══════════════════════════════════════════════════════════ */
/*  THE UPSI MAILBOX                                          */
/* ═══════════════════════════════════════════════════════════ */

const MAILBOX_STATUS = {
  never_connected: { label: 'Not tested yet', tone: '#8a6a1f' },
  ok: { label: 'Connected', tone: '#4a6b3f' },
  error: { label: 'Error', tone: '#9a564a' },
  disabled: { label: 'Switched off', tone: '#a09684' },
};

const emptyMailboxForm = { upsiAddress: '', host: '', port: 993, secure: true, user: '', folder: 'INBOX', password: '', enabled: true };

/**
 * Mail providers, so the officer picks one rather than looking up a hostname.
 * `hint` is the one thing that trips people up at each: a normal password is
 * refused, and the app password has to be created first.
 */
const MAIL_PROVIDERS = [
  { key: 'google', label: 'Google Workspace / Gmail', host: 'imap.gmail.com', hint: 'Turn on 2-step verification for the account, then create an app password at myaccount.google.com → Security → App passwords. Use that 16-character password here, not the normal one.' },
  { key: 'microsoft', label: 'Microsoft 365 / Outlook', host: 'outlook.office365.com', hint: 'IMAP must be enabled for the mailbox in the Microsoft 365 admin centre. If the account uses 2-step verification, create an app password under Security info.' },
  { key: 'zoho', label: 'Zoho Mail', host: 'imap.zoho.in', hint: 'Enable IMAP in Zoho Mail → Settings → Mail Accounts, and create an application-specific password under Security.' },
  { key: 'other', label: 'Other IMAP server', host: '', hint: 'Ask your IT team for the IMAP server name and port (usually port 993 with TLS) and whether an app password is needed.' },
];

const providerFor = (host) => MAIL_PROVIDERS.find((p) => p.host && p.host === String(host || '').trim().toLowerCase())
  || (host ? MAIL_PROVIDERS[MAIL_PROVIDERS.length - 1] : null);

/** The provider buttons with the one-line "how to get a password" for the chosen one. */
function ProviderPicker({ host, onPick }) {
  const chosen = providerFor(host);
  return (
    <div className="md:col-span-2">
      <div className={labelClass}>Mail provider</div>
      <div className="flex gap-2 flex-wrap mt-1.5">
        {MAIL_PROVIDERS.map((p) => {
          const active = chosen?.key === p.key;
          return (
            <button
              key={p.key}
              type="button"
              aria-pressed={active}
              onClick={() => onPick(p)}
              className={`px-3 py-1.5 rounded-full border text-[12px] transition-colors ${active ? 'border-[#145f5f] bg-[#145f5f] text-white' : 'border-[#ddd6c6] bg-white text-[#4a4034] hover:border-[#145f5f]'}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {chosen && <div className="text-[11.5px] text-[#7a7060] leading-relaxed mt-2 max-w-2xl">{chosen.hint}</div>}
    </div>
  );
}

/**
 * The company's UPSI mailbox — one per company, connected here by its own
 * compliance officer. A user with several companies connects one on each; the
 * card always shows the company selected in the sidebar.
 *
 * Only the officer sees the form (manageMailbox); contributors see status. The
 * password is encrypted on save and never returned — the "Password" field on
 * an update is blank and blank means "keep the current one".
 */
function MailboxPanel({ companyId, can, showToast, startSignal = 0 }) {
  const [mailbox, setMailbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyMailboxForm);
  const [busy, setBusy] = useState('');
  const [showHow, setShowHow] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sddAPI.getMailbox(companyId);
      setMailbox(res?.data?.mailbox || null);
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, showToast]);

  useEffect(() => { setEditing(false); load(); }, [load]);

  const startEdit = () => {
    setForm(mailbox
      ? { upsiAddress: mailbox.upsiAddress, host: mailbox.imap.host, port: mailbox.imap.port, secure: mailbox.imap.secure, user: mailbox.imap.user, folder: mailbox.imap.folder || 'INBOX', password: '', enabled: mailbox.enabled }
      : emptyMailboxForm);
    setEditing(true);
  };

  // The "Connect a mailbox" chooser opens this form by bumping the signal.
  useEffect(() => {
    if (startSignal && can.manageMailbox) startEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSignal]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const run = async (kind, call, after) => {
    setBusy(kind);
    try {
      const res = await call();
      after?.(res);
      showToast(res?.message || 'Done.', 'success');
    } catch (error) {
      showToast(errText(error), 'error');
      load();
    } finally {
      setBusy('');
    }
  };

  const save = () => run('save', () => sddAPI.setMailbox(companyId, { ...form, port: Number(form.port) }), (res) => { setMailbox(res?.data?.mailbox || null); setEditing(false); });

  // Save, then test the login straight away — one click instead of two, and a wrong password shows now.
  const saveAndTest = async () => {
    setBusy('save');
    try {
      const saved = await sddAPI.setMailbox(companyId, { ...form, port: Number(form.port) });
      setMailbox(saved?.data?.mailbox || null);
      setEditing(false);
      try {
        const tested = await sddAPI.testMailbox(companyId);
        showToast(tested?.message || 'Connected.', 'success');
      } catch (error) {
        showToast(`Saved, but the connection test failed: ${errText(error)}`, 'error');
      }
      load();
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy('');
    }
  };
  const test = () => run('test', () => sddAPI.testMailbox(companyId), () => load());
  const sync = () => run('sync', () => sddAPI.syncMailbox(companyId), (res) => setMailbox(res?.data?.mailbox || mailbox));

  if (loading) return null;

  const status = MAILBOX_STATUS[mailbox?.status] || MAILBOX_STATUS.never_connected;

  return (
    <Card className="mb-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <TbMail className="text-[#8a6a1f]" />
            <span className="text-[13px] font-semibold">UPSI mailbox</span>
            {mailbox ? <Chip tone={status.tone}>{status.label}</Chip> : <Chip tone="#a09684">Not connected</Chip>}
          </div>
          {mailbox ? (
            <div className="text-[12px] text-[#7a7060] leading-relaxed">
              Email copied to <span className="font-mono text-[#4a4034]">{mailbox.upsiAddress}</span> is read every minute.
              With the matter code in the subject (e.g. <span className="font-mono">[UPSI-12]</span>) it seals straight into the
              register; otherwise it waits below. {mailbox.observed} email{mailbox.observed === 1 ? '' : 's'} read so far
              {mailbox.lastSyncAt && <> · last read {new Date(mailbox.lastSyncAt).toLocaleString('en-IN')}</>}.
              {mailbox.status === 'error' && mailbox.lastError && (
                <div className="mt-1 text-[#9a564a]">Last error: {mailbox.lastError}</div>
              )}
            </div>
          ) : (
            <div className="text-[12px] text-[#7a7060] leading-relaxed max-w-2xl">
              One mailbox for this company, connected once. Your insiders set up nothing — they add the address in Cc
              and the matter code in the subject when they email UPSI, and the entry seals itself into the register with
              sender, recipients, time and attachment names taken from the email. Only those details are read; the
              message body never is.
              {!can.manageMailbox && <> Only the company owner can connect it.</>}
            </div>
          )}
          <button type="button" className="text-[11px] text-[#8a6a1f] underline mt-2" onClick={() => setShowHow((v) => !v)}>
            {showHow ? 'Hide' : 'How to set this up'}
          </button>
          {showHow && (
            <ol className="mt-2 text-[12px] text-[#4a4034] leading-relaxed list-decimal pl-5 space-y-1 max-w-2xl">
              <li>Create a dedicated mailbox on your company mail system, e.g. <span className="font-mono">upsi@yourcompany.com</span>. Restrict its login to the Compliance Officer.</li>
              <li>Enable IMAP on it. On Microsoft 365 or Google, turn on 2-step verification for that mailbox and create an <em>app password</em> — a normal password is refused.</li>
              <li>Enter the address, server, login and password below, then <em>Test connection</em>.</li>
              <li>Create each UPSI matter on the <em>UPSI Events</em> tab with a standing purpose. It gets a code such as <span className="font-mono">UPSI-12</span>.</li>
              <li>Tell insiders: when emailing UPSI, put <span className="font-mono">upsi@yourcompany.com</span> in Cc and the code in the subject. That is all they do.</li>
              <li>Set a 30-day auto-delete on the mailbox — the register entry is the 8-year record, not the mail.</li>
            </ol>
          )}
        </div>
        {can.manageMailbox && !editing && (
          <div className="flex gap-2 flex-wrap">
            {mailbox && mailbox.enabled && (
              <>
                <Button variant="outline" size="sm" onClick={test} loading={busy === 'test'} disabled={!!busy && busy !== 'test'}>Test connection</Button>
                <Button variant="outline" size="sm" onClick={sync} loading={busy === 'sync'} disabled={!!busy && busy !== 'sync'}>Read now</Button>
              </>
            )}
            {mailbox && <Button size="sm" onClick={startEdit} disabled={!!busy}>Settings</Button>}
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-5 pt-5 border-t border-[#e8e2d6]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProviderPicker host={form.host} onPick={(p) => setForm((f) => ({ ...f, host: p.host, port: 993, secure: true }))} />
            <Field label="UPSI address (the one insiders copy)" required className="md:col-span-2">
              <TextField type="email" value={form.upsiAddress} onChange={set('upsiAddress')} placeholder="upsi@yourcompany.com" />
            </Field>
            <Field label="Mail server (IMAP)" required>
              <TextField value={form.host} onChange={set('host')} placeholder="imap.yourcompany.com" />
            </Field>
            <Field label="Mailbox login" required>
              <TextField value={form.user} onChange={set('user')} placeholder="Usually the same as the address" autoComplete="off" />
            </Field>
            <Field label={mailbox?.hasPassword ? 'Password (leave blank to keep the current one)' : 'Password'} required={!mailbox?.hasPassword}>
              <TextField type="password" value={form.password} onChange={set('password')} autoComplete="new-password" />
            </Field>
            <Field label="Folder">
              <TextField value={form.folder} onChange={set('folder')} placeholder="INBOX" />
            </Field>
            <Field label="Port" required>
              <TextField type="number" value={form.port} onChange={set('port')} />
            </Field>
            <div className="flex flex-col justify-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-[12px] text-[#4a4034] cursor-pointer">
                <input type="checkbox" checked={form.secure} onChange={set('secure')} className="h-4 w-4 accent-[#145f5f]" /> Use TLS (port 993)
              </label>
              <label className="flex items-center gap-2 text-[12px] text-[#4a4034] cursor-pointer">
                <input type="checkbox" checked={form.enabled} onChange={set('enabled')} className="h-4 w-4 accent-[#145f5f]" /> Read this mailbox
              </label>
            </div>
          </div>
          <div className="text-[11px] text-[#7a7060] mt-3 leading-relaxed">
            These are the details of the <span className="font-semibold">UPSI mailbox only</span> — not of any insider.
            The password is stored encrypted and never shown again, to anyone.
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={saveAndTest} loading={busy === 'save'} disabled={!!busy && busy !== 'save'}>Save and test</Button>
            <Button variant="outline" onClick={save} disabled={!!busy}>Save only</Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={!!busy}>Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

const emptyWatchForm = { address: '', host: '', port: 993, secure: true, user: '', folder: '', password: '', matchInsiderRecipients: false, watchInbox: false, aiClassify: false, consent: false, extendedConsent: false };

/**
 * Sent-mail watches — people's Sent folders, read for UPSI nobody copied to the
 * UPSI address. The shared mailbox above only sees what insiders remember to
 * Cc; a watch closes that gap for the people the officer chooses.
 *
 * Narrow by design, and the screen says so: only emails with the matter code or
 * [UPSI] in the subject (or, if ticked, a recipient on the insider list) are
 * picked up, only headers are read, and the mailbox owner's consent is recorded
 * when it is connected. Only the officer sees the form (manageSentWatch).
 */
function SentWatchPanel({ companyId, can, showToast, startSignal = 0 }) {
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyWatchForm);
  // Settings on a watch that already exists: its consent is on record and its password is kept when left blank.
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sddAPI.listSentWatches(companyId);
      setWatches(res?.data?.watches || []);
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, showToast]);

  useEffect(() => { setEditing(false); load(); }, [load]);

  // The "Connect a mailbox" chooser opens this form by bumping the signal.
  useEffect(() => {
    if (startSignal && can.manageSentWatch) { setExisting(null); setForm(emptyWatchForm); setEditing(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSignal]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const run = async (kind, call, after) => {
    setBusy(kind);
    try {
      const res = await call();
      after?.(res);
      showToast(res?.message || 'Done.', 'success');
    } catch (error) {
      showToast(errText(error), 'error');
      load();
    } finally {
      setBusy('');
    }
  };

  const save = () => run('save', () => sddAPI.saveSentWatch(companyId, { ...form, port: Number(form.port) }), () => { setEditing(false); setExisting(null); setForm(emptyWatchForm); load(); });

  const saveAndTest = async () => {
    setBusy('save');
    try {
      const saved = await sddAPI.saveSentWatch(companyId, { ...form, port: Number(form.port) });
      const id = saved?.data?.watch?.id;
      setEditing(false);
      setExisting(null);
      setForm(emptyWatchForm);
      if (id) {
        try {
          const tested = await sddAPI.testSentWatch(companyId, id);
          showToast(tested?.message || 'Connected.', 'success');
        } catch (error) {
          showToast(`Saved, but the connection test failed: ${errText(error)}`, 'error');
        }
      } else {
        showToast(saved?.message || 'Saved.', 'success');
      }
      load();
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy('');
    }
  };
  const test = (w) => run(`test-${w.id}`, () => sddAPI.testSentWatch(companyId, w.id), () => load());
  const sync = (w) => run(`sync-${w.id}`, () => sddAPI.syncSentWatch(companyId, w.id), () => load());
  const settings = (w) => {
    setExisting(w);
    setForm({
      address: w.address,
      host: w.imap.host,
      port: w.imap.port,
      secure: w.imap.secure,
      user: w.imap.user,
      folder: w.imap.folder || '',
      password: '',
      matchInsiderRecipients: !!w.matchInsiderRecipients,
      watchInbox: !!w.watchInbox,
      aiClassify: !!w.aiClassify,
      consent: true,
      extendedConsent: !!(w.consent && w.consent.extendedAt),
    });
    setEditing(true);
  };

  // A new watch needs the Sent-folder consent; turning the Inbox or AI on needs the wider one.
  const widerOn = form.watchInbox || form.aiClassify;
  const canSave = (existing || form.consent) && (!widerOn || form.extendedConsent);

  const remove = (w) => {
    if (!window.confirm(`Stop watching ${w.address}? What was already recorded stays in the register.`)) return;
    run(`remove-${w.id}`, () => sddAPI.removeSentWatch(companyId, w.id), () => load());
  };

  if (loading) return null;

  return (
    <Card className="mb-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <TbMail className="text-[#8a6a1f]" />
            <span className="text-[13px] font-semibold">Sent-mail watch</span>
            <Chip tone={watches.length ? '#4a6b3f' : '#a09684'}>{watches.length ? `${watches.length} watched` : 'None'}</Chip>
          </div>
          <div className="text-[12px] text-[#7a7060] leading-relaxed max-w-2xl">
            Catches UPSI that nobody copied to the UPSI address. Connect the mailbox of someone who sends UPSI — the CFO,
            the Company Secretary — and the emails they send with <span className="font-mono">[UPSI-12]</span> or{' '}
            <span className="font-mono">[UPSI]</span> in the subject are picked up from their Sent folder, Bcc included, and
            handled exactly like an email copied to the UPSI address. Only headers and attachment names are read — never the
            body — and nothing else in the mailbox is touched.
          </div>
        </div>
      </div>

      {watches.length > 0 && (
        <ul className="mt-4 divide-y divide-[#e8e2d6]">
          {watches.map((w) => {
            const status = MAILBOX_STATUS[w.status] || MAILBOX_STATUS.never_connected;
            return (
              <li key={w.id} className="py-3 flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 text-[12px] text-[#4a4034] leading-relaxed">
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{w.address}</span>
                    <Chip tone={status.tone}>{status.label}</Chip>
                  </div>
                  <div className="text-[#7a7060]">
                    {w.observed} picked up of {w.scanned} email{w.scanned === 1 ? '' : 's'} read
                    {w.lastSyncAt && <> · last read {new Date(w.lastSyncAt).toLocaleString('en-IN')}</>}
                    {w.matchInsiderRecipients && <> · also matches insider recipients</>}
                    {w.watchInbox && <> · Inbox also read</>}
                  </div>
                  {w.aiClassify && (
                    <div className="text-[#7a7060]">
                      AI is on: {w.aiFlagged} flagged as likely UPSI of {w.aiAsked} untagged email{w.aiAsked === 1 ? '' : 's'} judged. Anything it flags waits in the list below for a person to confirm.
                    </div>
                  )}
                  {w.aiClassify && w.aiLog && w.aiLog.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[11.5px] text-[#8a6a1f] underline">Recent AI decisions</summary>
                      <ul className="mt-1.5 space-y-1.5 text-[11.5px] text-[#4a4034]">
                        {w.aiLog.map((e) => (
                          <li key={`${e.at}-${e.subject}`} className="leading-snug">
                            <span className="font-semibold">{e.subject || '(no subject)'}</span>{' '}
                            <span className="text-[#7a7060]">({e.direction === 'received' ? 'received' : 'sent'})</span>
                            <br />
                            {e.error
                              ? <span className="text-[#9a564a]">Could not be judged: {e.error}</span>
                              : (
                                <span className={e.flagged ? 'text-[#145f5f]' : 'text-[#7a7060]'}>
                                  {e.flagged ? 'Flagged' : 'Not flagged'} — {e.isUpsi ? 'looks like UPSI' : 'not UPSI'}, {Math.round((e.confidence || 0) * 100)}% confident{e.reason ? `: ${e.reason}` : ''}
                                </span>
                              )}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {w.consent?.at && (
                    <div className="text-[11px] text-[#a09684]">
                      Consent recorded {new Date(w.consent.at).toLocaleDateString('en-IN')}{w.consent.byName && <> by {w.consent.byName}</>}
                    </div>
                  )}
                  {w.status === 'error' && w.lastError && <div className="mt-1 text-[#9a564a]">Last error: {w.lastError}</div>}
                </div>
                {can.manageSentWatch && (
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => test(w)} loading={busy === `test-${w.id}`} disabled={!!busy && busy !== `test-${w.id}`}>Test connection</Button>
                    <Button variant="outline" size="sm" onClick={() => sync(w)} loading={busy === `sync-${w.id}`} disabled={!!busy && busy !== `sync-${w.id}`}>Read now</Button>
                    <Button variant="outline" size="sm" onClick={() => settings(w)} disabled={!!busy}>Settings</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(w)} disabled={!!busy}>Stop watching</Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <div className="mt-5 pt-5 border-t border-[#e8e2d6]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProviderPicker host={form.host} onPick={(p) => setForm((f) => ({ ...f, host: p.host, port: 993, secure: true }))} />
            <Field label="Mailbox to watch (whose Sent folder)" required className="md:col-span-2">
              <TextField type="email" value={form.address} onChange={set('address')} placeholder="cfo@yourcompany.com" disabled={!!existing} />
            </Field>
            <Field label="Mail server (IMAP)" required>
              <TextField value={form.host} onChange={set('host')} placeholder="imap.yourcompany.com" />
            </Field>
            <Field label="Mailbox login">
              <TextField value={form.user} onChange={set('user')} placeholder="Blank = the mailbox address" autoComplete="off" />
            </Field>
            <Field label={existing ? 'Password (leave blank to keep the current one)' : 'Password (an app password on Microsoft 365 or Google)'} required={!existing}>
              <TextField type="password" value={form.password} onChange={set('password')} autoComplete="new-password" />
            </Field>
            <Field label="Sent folder (blank = detect)">
              <TextField value={form.folder} onChange={set('folder')} placeholder="[Gmail]/Sent Mail" />
            </Field>
            <Field label="Port" required>
              <TextField type="number" value={form.port} onChange={set('port')} />
            </Field>
            <div className="flex flex-col justify-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-[12px] text-[#4a4034] cursor-pointer">
                <input type="checkbox" checked={form.secure} onChange={set('secure')} className="h-4 w-4 accent-[#145f5f]" /> Use TLS (port 993)
              </label>
              <label className="flex items-center gap-2 text-[12px] text-[#4a4034] cursor-pointer">
                <input type="checkbox" checked={form.matchInsiderRecipients} onChange={set('matchInsiderRecipients')} className="h-4 w-4 accent-[#145f5f]" />
                Also pick up emails to anyone on the insider list, even without the tag
              </label>
            </div>
            <div className="md:col-span-2 rounded-[10px] border border-[#e8e2d6] bg-[#faf7ef] p-3.5">
              <div className="text-[12px] font-semibold text-[#08111f] mb-1">Catch UPSI that has no tag</div>
              <div className="text-[11.5px] text-[#7a7060] leading-relaxed mb-2.5">
                Optional. Without these, only emails carrying [UPSI] or the matter code are picked up.
              </div>
              <label className="flex items-start gap-2 text-[12px] text-[#4a4034] cursor-pointer leading-relaxed mb-1.5">
                <input type="checkbox" checked={form.watchInbox} onChange={set('watchInbox')} className="h-4 w-4 mt-0.5 accent-[#145f5f]" />
                <span><span className="font-semibold">Also read incoming mail (Inbox).</span> UPSI often arrives — draft results from the auditor, a term sheet from a banker.</span>
              </label>
              <label className="flex items-start gap-2 text-[12px] text-[#4a4034] cursor-pointer leading-relaxed">
                <input type="checkbox" checked={form.aiClassify} onChange={set('aiClassify')} className="h-4 w-4 mt-0.5 accent-[#145f5f]" />
                <span>
                  <span className="font-semibold">Let AI flag emails that look like UPSI.</span> For each untagged email the AI sees only
                  the subject, sender, recipients and attachment names — never the body — and judges whether it looks like UPSI.
                  What it flags goes to the list below as a suggestion with its reason; a person confirms it. The AI never
                  writes to the register.
                </span>
              </label>
              {widerOn && (
                <label className="flex items-start gap-2 text-[12px] text-[#4a4034] cursor-pointer leading-relaxed mt-2.5 pt-2.5 border-t border-[#e8e2d6]">
                  <input type="checkbox" checked={form.extendedConsent} onChange={set('extendedConsent')} className="h-4 w-4 mt-0.5 accent-[#145f5f]" />
                  <span>
                    The owner of this mailbox also agrees to the above: the Inbox being read, and the subject, sender, recipients
                    and attachment names of untagged emails being sent to an AI service. I am recording that agreement here.
                  </span>
                </label>
              )}
            </div>
            {!existing && (
              <label className="md:col-span-2 flex items-start gap-2 text-[12px] text-[#4a4034] cursor-pointer leading-relaxed">
                <input type="checkbox" checked={form.consent} onChange={set('consent')} className="h-4 w-4 mt-0.5 accent-[#145f5f]" />
                <span>
                  The owner of this mailbox agrees that its Sent folder is read for emails marked as UPSI. Only headers and
                  attachment names are read, never the body, and nothing in the mailbox is changed. I am recording that
                  agreement here.
                </span>
              </label>
            )}
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={saveAndTest} loading={busy === 'save'} disabled={!canSave || (!!busy && busy !== 'save')}>Save and test</Button>
            <Button variant="outline" onClick={save} disabled={!canSave || !!busy}>Save only</Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={!!busy}>Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}


/**
 * "Connect a mailbox" — one front door for both ways of capturing email.
 *
 * The two cards say what each does in plain words and what the person has to
 * do afterwards, so nobody has to know the difference between the shared UPSI
 * address and a sent-mail watch before clicking. Choosing one opens that form
 * on the panel below. A person who cannot connect anything (not the officer)
 * sees the status panels but no button.
 */
function MailCapture({ companyId, can, showToast }) {
  const [choosing, setChoosing] = useState(false);
  const [sharedSignal, setSharedSignal] = useState(0);
  const [watchSignal, setWatchSignal] = useState(0);

  const canConnect = can.manageMailbox || can.manageSentWatch;

  const choose = (kind) => {
    setChoosing(false);
    if (kind === 'shared') setSharedSignal((n) => n + 1);
    else setWatchSignal((n) => n + 1);
  };

  const options = [
    {
      kind: 'shared',
      title: 'Shared UPSI address',
      tag: 'Recommended first',
      allowed: can.manageMailbox,
      body: 'One mailbox for the company, such as upsi@yourcompany.com. Insiders copy it (Cc) whenever they email UPSI, and the entry records itself. Nothing for insiders to set up.',
      after: 'You enter that mailbox’s login once. Only the company owner can do this.',
    },
    {
      kind: 'watch',
      title: 'A person’s sent mail',
      tag: 'Catches a forgotten Cc',
      allowed: can.manageSentWatch,
      body: 'Watch the Sent folder of someone who sends UPSI, such as the CFO. Emails they send with [UPSI] or the matter code in the subject are picked up even if nobody copied the UPSI address.',
      after: 'Needs that person’s agreement, which you record when connecting. Add as many people as you need.',
    },
  ];

  return (
    <>
      {canConnect && (can.viewMailbox || can.viewSentWatch) && (
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="text-[12px] text-[#7a7060]">Email capture — how UPSI sent by email reaches this register.</div>
          <Button size="sm" onClick={() => setChoosing(true)}>Connect a mailbox</Button>
        </div>
      )}

      {can.viewMailbox && <MailboxPanel companyId={companyId} can={can} showToast={showToast} startSignal={sharedSignal} />}
      {can.viewSentWatch && <SentWatchPanel companyId={companyId} can={can} showToast={showToast} startSignal={watchSignal} />}

      <Modal open={choosing} onClose={() => setChoosing(false)} title="Connect a mailbox" bodyClassName="px-6 py-5">
        <div className="text-[12px] text-[#7a7060] mb-4">What do you want to capture? You can set up both.</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {options.map((o) => (
            <button
              key={o.kind}
              type="button"
              disabled={!o.allowed}
              onClick={() => choose(o.kind)}
              className="text-left rounded-[12px] border border-[#ddd6c6] bg-white p-4 transition-colors hover:border-[#145f5f] disabled:opacity-50 disabled:hover:border-[#ddd6c6] disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <TbMail className="text-[#8a6a1f]" />
                <span className="text-[13px] font-semibold text-[#08111f]">{o.title}</span>
              </div>
              <Chip tone="#8a6a1f">{o.tag}</Chip>
              <div className="text-[12px] text-[#4a4034] leading-relaxed mt-2">{o.body}</div>
              <div className="text-[11.5px] text-[#7a7060] leading-relaxed mt-2">{o.after}</div>
              {!o.allowed && <div className="text-[11.5px] text-[#9a564a] mt-2">You do not have permission to connect this.</div>}
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}

/**
 * Suggestions raised from activity inside the platform, waiting for a person.
 *
 * Nothing on this screen is in the register yet. Confirming is what writes —
 * and it demands the two things the platform cannot know: which UPSI event the
 * sharing belongs to, and the legitimate purpose under Reg 3(2A). The form is
 * built so neither can be skipped.
 */
function PendingTab({ companyId, events, can, onConfirmed, showToast }) {
  const [proposals, setProposals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ upsiEvent: '', purpose: '' });
  const [busy, setBusy] = useState(null);
  // Recipients added to the insider list during this visit. The stored proposal
  // still holds `insider: null` until confirmation re-matches server-side, so
  // this is what stops the panel nagging about someone just added.
  const [resolved, setResolved] = useState(new Set());
  const [addingFor, setAddingFor] = useState(null);
  // Events created from inside this panel. Held locally as well as refreshed on
  // the parent, so the new one is selectable immediately rather than after a
  // round trip — losing the purpose someone had already typed would be worse
  // than a stale list.
  const [newEvents, setNewEvents] = useState([]);
  const [creatingEvent, setCreatingEvent] = useState(false);
  // The queue is paged server-side. Oldest first, so the suggestion whose
  // recording is most overdue is the one on screen.
  const [page, setPage] = useState(1);
  const [paging, setPaging] = useState(null);
  // Dismissal is permanent, so it goes through a modal that says so rather than
  // a browser prompt sitting one keystroke from the record button.
  const [dismissing, setDismissing] = useState(null);

  const allEvents = useMemo(() => [...newEvents, ...events], [newEvents, events]);

  /**
   * The sender, shaped like a recipient.
   *
   * The register must name who shared the information as well as who received
   * it, so an unmatched sender blocks confirmation exactly as an unmatched
   * recipient does — and needs the same way out.
   */
  const senderOf = useCallback((proposal) => ({
    name: proposal.sharedByName || '',
    email: proposal.sharedByEmail || '',
    matched: !!proposal.sharedByInsider,
    isSender: true,
  }), []);

  const senderSettled = useCallback(
    (proposal) => {
      const sender = senderOf(proposal);
      return sender.matched || resolved.has(keyOf(sender));
    },
    [senderOf, resolved],
  );

  /** Everyone still without an identity — the server will refuse these too. */
  const unresolved = useCallback((proposal) => {
    const people = (proposal.recipients || [])
      .filter((r) => !r.matched && !resolved.has(keyOf(r)));
    if (!senderSettled(proposal)) people.unshift(senderOf(proposal));
    return people;
  }, [resolved, senderOf, senderSettled]);

  const canRecord = useCallback(
    (proposal) => form.upsiEvent && form.purpose.trim() && unresolved(proposal).length === 0,
    [form, unresolved],
  );

  /** Why the button is disabled. A dead end with no explanation is worse than none. */
  const blockedBecause = useCallback((proposal) => {
    const missing = unresolved(proposal);
    if (missing.length) {
      const names = missing.map((r) => r.name || r.email).join(', ');
      return `${names} must be on the insider list first — the register names the sender as well as the recipients.`;
    }
    if (!form.upsiEvent) return 'Choose the UPSI event this sharing concerns.';
    if (!form.purpose.trim()) return 'State the legitimate purpose.';
    return '';
  }, [form, unresolved]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sddAPI.getProposals(companyId, { page });
      setProposals(res?.data?.proposals || []);
      setSummary(res?.data?.summary || null);
      setPaging(res?.data?.page || null);
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, page, showToast]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (proposal) => {
    setBusy(proposal._id);
    try {
      const res = await sddAPI.confirmProposal(companyId, proposal._id, {
        upsiEvent: form.upsiEvent,
        purpose: form.purpose,
      });
      showToast(res?.message || 'Recorded.', 'success');
      setOpenId(null);
      setForm({ upsiEvent: '', purpose: '' });
      load();
      onConfirmed();
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (proposal, reason) => {
    setBusy(proposal._id);
    try {
      await sddAPI.dismissProposal(companyId, proposal._id, reason.trim());
      showToast('Dismissed. Nothing was written to the register.', 'success');
      setDismissing(null);
      load();
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <Card><Empty>Loading…</Empty></Card>;

  return (
    <>
      <p className="text-[12px] text-[#7a7060] mb-4 max-w-2xl leading-relaxed">
        An email copied to the company&apos;s UPSI address seals itself into the register when its subject
        carries the matter code (e.g. <span className="font-mono">[UPSI-12]</span>), the matter has a standing
        purpose, and everyone on it is on the insider list. Anything that could not seal itself waits here,
        saying what is missing — as does a document shared inside Compliance Saarthi, which always needs a
        person to name the matter and the purpose. Nothing here is in the register yet, and nothing here can be edited.
      </p>

      <MailCapture companyId={companyId} can={can} showToast={showToast} />

      {summary && summary.confirmed + summary.dismissed > 0 && (
        <div className="text-[11px] text-[#a09684] mb-4">
          {summary.confirmed} confirmed · {summary.dismissed} dismissed to date
        </div>
      )}

      {proposals.length === 0 ? (
        <Card>
          <Empty>
            <div className="text-[14px] font-semibold text-[#4a4034] mb-2">Nothing needs attention</div>
            <div className="text-[12px] max-w-md mx-auto leading-relaxed">
              Emails with a matter code seal themselves and go straight to the Register tab. Something
              appears here only when it could not — no code, an unknown code, a matter with no standing
              purpose, or a person not on the insider list — or when a document is shared inside
              Compliance Saarthi. Sharings elsewhere (a meeting, a call, an email without the copy)
              are recorded by hand on the Register tab.
            </div>
          </Empty>
        </Card>
      ) : (
        <div className="space-y-3">
          {proposals.map((proposal) => (
            <Card key={proposal._id} className={openId === proposal._id ? 'border-[#8a6a1f]' : ''}>
              {/* Header: what moved, between whom, when. The four facts the
                  platform observed, laid out as facts rather than prose. */}
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap mb-2">
                    <span className="text-[14px] font-semibold">
                      {proposal.sourceLabel || 'Untitled document'}
                    </span>
                    {/* Titles repeat. The reference is what tells two
                        suggestions apart, and it is the handle shown on the
                        document itself. */}
                    {proposal.sourceDocumentRef && (
                      <span className="text-[11px] font-mono text-[#a09684]">
                        {proposal.sourceDocumentRef}
                      </span>
                    )}
                    {proposal.sourceDocumentType && (
                      <Chip tone="#a09684">{docTypeLabel(proposal.sourceDocumentType)}</Chip>
                    )}
                  </div>

                  {proposal.waitingOn && (
                    <div className="text-[12px] text-[#8a6a1f] bg-[#fbf6e9] border border-[#efe3c2] rounded px-2.5 py-1.5 mb-2 leading-snug">
                      <span className="font-semibold">Could not seal itself: </span>{proposal.waitingOn}
                    </div>
                  )}

                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px]">
                    <dt className="text-[#a09684]">Shared by</dt>
                    <dd>
                      <span
                        className={`inline-flex items-center px-2 py-[1px] rounded-full text-[11px] ${
                          senderSettled(proposal)
                            ? 'bg-[#e8efe5] text-[#4a6b3f]'
                            : 'bg-[#f2e9e6] text-[#9a564a]'
                        }`}
                      >
                        {proposal.sharedByName || 'Unknown'}
                        {!senderSettled(proposal) && ' · not an insider'}
                      </span>
                    </dd>

                    <dt className="text-[#a09684]">With</dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {(proposal.recipients || []).map((r, i) => {
                        const settled = r.matched || resolved.has(keyOf(r));
                        return (
                          <span
                            key={i}
                            className={`inline-flex items-center gap-1 px-2 py-[1px] rounded-full text-[11px] ${
                              settled
                                ? 'bg-[#e8efe5] text-[#4a6b3f]'
                                : 'bg-[#f2e9e6] text-[#9a564a]'
                            }`}
                          >
                            {r.name || r.email}
                            {!settled && ' · not an insider'}
                          </span>
                        );
                      })}
                    </dd>

                    <dt className="text-[#a09684]">When</dt>
                    <dd className="text-[#4a4034]">{fmtDateTime(proposal.sharedAt)}</dd>

                    <dt className="text-[#a09684]">How</dt>
                    <dd className="text-[#4a4034]">{howShared(proposal)}</dd>
                  </dl>
                </div>

                {can.recordSharing && openId !== proposal._id && (
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => {
                        setOpenId(proposal._id);
                        setForm({ upsiEvent: proposal.upsiEvent || '', purpose: '' });
                      }}
                    >
                      Review &amp; record
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDismissing(proposal)}>
                      Not UPSI
                    </Button>
                  </div>
                )}
              </div>

              {openId === proposal._id && (
                <div className="mt-4 pt-4 border-t border-[#e8e2d6]">
                  {/* Everything below is what the platform could NOT know. Two
                      fields, both required, both human judgments. */}
                  <div className="text-[11px] uppercase tracking-[0.08em] text-[#a09684] mb-3">
                    Two things to add before this can be recorded
                  </div>

                  {unresolved(proposal).map((recipient) => (
                    <div
                      key={keyOf(recipient)}
                      className="mb-3 p-3 rounded-lg bg-[#f2e9e6] flex items-center gap-3 flex-wrap"
                    >
                      <div className="text-[12px] text-[#9a564a] min-w-0 flex-1">
                        <strong>{recipient.name || recipient.email}</strong>
                        {recipient.isSender
                          ? ' shared this information, so the register must name them. They need a PAN first.'
                          : ' needs a PAN before they can be named in the register.'}
                      </div>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => setAddingFor({ proposalId: proposal._id, recipient })}
                      >
                        Add to insiders
                      </Button>
                    </div>
                  ))}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Field label="Which UPSI event" required>
                        <Select
                          value={form.upsiEvent}
                          onChange={(value) => setForm((f) => ({ ...f, upsiEvent: value }))}
                          options={allEvents.map((e) => ({
                            value: e._id,
                            label: `${e.title}${e.status === 'published' ? ' (published)' : ''}`,
                          }))}
                          placeholder={allEvents.length ? '— Select —' : 'None yet'}
                        />
                      </Field>
                      <div className="mt-1.5">
                        {allEvents.length === 0 && (
                          <div className="text-[11px] text-[#9a564a] mb-1">
                            This company has no UPSI events yet.
                          </div>
                        )}
                        {can.manageEvents && (
                          <button
                            type="button"
                            className="text-[11px] font-semibold text-[#8a6a1f] hover:underline"
                            onClick={() => setCreatingEvent(true)}
                          >
                            + Create a UPSI event
                          </button>
                        )}
                      </div>
                    </div>

                    <div>
                      <span className={labelClass}>Legitimate purpose *</span>
                      <textarea
                        className={textareaClass}
                        rows={3}
                        value={form.purpose}
                        onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
                        placeholder="Why was this information shared with them?"
                      />
                      <div className="text-[11px] text-[#a09684] mt-1">
                        Reg 3(2A). For example: professional advisory and preparation of board
                        documentation.
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-5 flex-wrap">
                    <Button
                      loading={busy === proposal._id}
                      disabled={!canRecord(proposal)}
                      onClick={() => confirm(proposal)}
                    >
                      Record in register
                    </Button>
                    <Button variant="ghost" onClick={() => setOpenId(null)}>Cancel</Button>

                    {/* A disabled button that will not say why is a dead end. */}
                    {!canRecord(proposal) && (
                      <span className="text-[11px] text-[#9a564a]">
                        {blockedBecause(proposal)}
                      </span>
                    )}
                  </div>

                  <div className="text-[11px] text-[#a09684] mt-3 leading-relaxed">
                    Recording seals this into the register permanently and sends each recipient the
                    notice Reg 3(2B) requires. It cannot be edited afterwards — only superseded.
                  </div>
                </div>
              )}
            </Card>
          ))}

          {paging && paging.pages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-[12px] text-[#7a7060]">
                {(paging.page - 1) * paging.limit + 1}–
                {Math.min(paging.page * paging.limit, paging.total)} of {paging.total},
                oldest first
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={paging.page <= 1}
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!paging.hasMore}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <CreateEventModal
        companyId={companyId}
        open={creatingEvent}
        onClose={() => setCreatingEvent(false)}
        onCreated={(event) => {
          // Selected straight away, so the purpose already typed survives.
          setNewEvents((prev) => [event, ...prev]);
          setForm((f) => ({ ...f, upsiEvent: event._id }));
          setCreatingEvent(false);
          onConfirmed(); // refresh the parent's list in the background
          showToast('UPSI event created and selected.', 'success');
        }}
        showToast={showToast}
      />

      <AddInsiderModal
        companyId={companyId}
        request={addingFor}
        onClose={() => setAddingFor(null)}
        onAdded={(recipient) => {
          // Mark resolved and close, leaving the confirm panel exactly as it
          // was — event and purpose intact. Nothing is sealed: the officer
          // still presses Record in register.
          setResolved((prev) => new Set(prev).add(keyOf(recipient)));
          setAddingFor(null);
          showToast('Added to the insider list. You can record the entry now.', 'success');
        }}
        showToast={showToast}
      />

      <DismissProposalModal
        proposal={dismissing}
        busy={busy === dismissing?._id}
        onClose={() => setDismissing(null)}
        onDismiss={dismiss}
      />
    </>
  );
}

/**
 * Create the UPSI event this sharing belongs to, without leaving the queue.
 *
 * A suggestion is raised the moment a document moves, which is often before
 * anyone has set up the matter it concerns — especially the first time a
 * company uses the register, when there are no events at all. Sending the
 * officer to another tab would lose the legitimate purpose they had already
 * typed, and that is the one field nobody wants to write twice.
 *
 * Created unpublished, always. Publication fixes the instant information ceased
 * to be UPSI; it is a separate, irreversible decision that belongs to whoever
 * knows the announcement actually went out.
 */
function CreateEventModal({ companyId, open, onClose, onCreated, showToast }) {
  const [form, setForm] = useState({ title: '', category: 'financial_results', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ title: '', category: 'financial_results', description: '' });
  }, [open]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      const res = await sddAPI.createEvent(companyId, {
        title: form.title.trim(),
        category: form.category,
        description: form.description.trim(),
      });
      const event = res?.data?.event;
      if (!event?._id) throw new Error('The event was not returned.');
      onCreated(event);
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Create a UPSI event"
      bodyClassName="px-6 py-5"
      footer={(
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving} disabled={!form.title.trim()}>Create</Button>
        </div>
      )}
    >
      <p className="text-[12px] text-[#7a7060] mb-4 leading-relaxed">
        The matter this sharing concerns — the information itself, not the document. Name it the way
        the company would: <em>Q3 FY26 unaudited results</em>, <em>Proposed acquisition of Vertex</em>.
      </p>

      <Field label="Title" required>
        <TextField
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Q3 FY26 unaudited results"
        />
      </Field>

      <div className="mt-4">
        <Field label="Category" required>
          <Select
            value={form.category}
            onChange={(value) => setForm((f) => ({ ...f, category: value }))}
            options={UPSI_CATEGORIES}
          />
        </Field>
      </div>

      <div className="mt-4">
        <span className={labelClass}>Description</span>
        <textarea
          className={textareaClass}
          rows={2}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </div>

      <div className="text-[11px] text-[#a09684] mt-4 leading-relaxed">
        Created <strong>unpublished</strong> — it stays UPSI until someone records that the
        announcement went out, on the UPSI Events tab. That step is irreversible, so it is not done
        for you here.
      </div>
    </Modal>
  );
}

/**
 * Add someone to the insider list without leaving the queue.
 *
 * Prefilled from what the platform observed, but never auto-created from it: a
 * name and an email are not an identity for Reg 3(5) purposes. The regulation
 * wants a PAN, or another identifier authorised by law where no PAN exists, and
 * an entry naming someone the company cannot produce one for is not much of a
 * record. So the form asks, and will not save without one.
 *
 * It also does not seal anything. Adding a person and recording a sharing are
 * two different decisions, and the second belongs to the compliance officer
 * after they have read what they are confirming.
 */
/**
 * Dismissing a suggestion, with the consequence stated.
 *
 * This replaced a `window.prompt`, and the reason is not polish. Dismissal is
 * permanent — there is no un-dismiss, and the unique index on the observed
 * action means re-sharing the same document to the same people will not raise
 * the suggestion again. A one-way action of that kind should not sit one
 * keystroke away from the record button behind a dialog that says nothing about
 * what it does.
 *
 * The reason is required because it is the company's evidence, later, that a
 * person looked at this sharing and judged it — the difference between a
 * considered decision and an omission.
 */
function DismissProposalModal({ proposal, onClose, onDismiss, busy }) {
  const [reason, setReason] = useState('');

  useEffect(() => { setReason(''); }, [proposal]);

  if (!proposal) return null;

  const recipients = (proposal.recipients || [])
    .map((r) => r.name || r.email)
    .filter(Boolean)
    .join(', ');

  return (
    <Modal
      open
      onClose={onClose}
      title="Not a UPSI sharing"
      bodyClassName="px-6 py-5"
      footer={(
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>Keep it in the queue</Button>
          <Button
            variant="danger"
            onClick={() => onDismiss(proposal, reason)}
            loading={busy}
            disabled={!reason.trim()}
          >
            Dismiss permanently
          </Button>
        </div>
      )}
    >
      <div className="mb-4 p-3 rounded-lg bg-[#f2e9e6] text-[12px] text-[#9a564a] leading-relaxed">
        This cannot be undone. The suggestion will not come back, and re-sharing the same
        document to the same people will not raise it again — the platform treats that as the
        same act of sharing. If this really was a UPSI sharing, record it instead.
      </div>

      <dl className="text-[12px] text-[#5c5346] mb-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <dt className="text-[#a09684]">Document</dt>
        <dd>{proposal.sourceLabel || 'Untitled draft'}</dd>
        {proposal.sourceDocumentRef && (
          <>
            <dt className="text-[#a09684]">Reference</dt>
            <dd>{proposal.sourceDocumentRef}</dd>
          </>
        )}
        <dt className="text-[#a09684]">Shared with</dt>
        <dd>{recipients || '—'}</dd>
      </dl>

      <Field label="Why is this not a UPSI sharing?" required>
        <textarea
          className={textareaClass}
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Routine board resolution, no price-sensitive information."
        />
        <p className="text-[11px] text-[#a09684] mt-1.5 leading-relaxed">
          Kept as the record that someone considered this sharing and decided about it.
        </p>
      </Field>
    </Modal>
  );
}

function AddInsiderModal({ companyId, request, onClose, onAdded, showToast }) {
  const [form, setForm] = useState({ name: '', pan: '', alternateId: '', alternateIdType: 'passport', category: 'external_other', organisation: '' });
  const [saving, setSaving] = useState(false);

  const recipient = request?.recipient;

  useEffect(() => {
    if (!recipient) return;
    setForm({
      name: recipient.name || '',
      pan: '',
      alternateId: '',
      alternateIdType: 'passport',
      category: recipient.isSender ? 'designated_person' : 'external_other',
      organisation: '',
    });
  }, [recipient]);

  if (!request) return null;

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const canSave = form.name.trim() && (form.pan.trim() || form.alternateId.trim());

  const save = async () => {
    setSaving(true);
    try {
      await sddAPI.createInsider(companyId, {
        name: form.name.trim(),
        // The sender is the company's own person, not an outside party — a
        // sensible default that the officer can still change.
        category: form.category,
        pan: form.pan.trim() || undefined,
        alternateId: form.alternateId.trim() || undefined,
        alternateIdType: form.alternateId.trim() ? form.alternateIdType : undefined,
        organisation: form.organisation.trim(),
        email: recipient?.email || '',
        isExternal: form.category.startsWith('external_'),
      });
      onAdded(recipient);
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Add to the insider list"
      bodyClassName="px-6 py-5"
      footer={(
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving} disabled={!canSave}>Add insider</Button>
        </div>
      )}
    >
      <p className="text-[12px] text-[#7a7060] mb-4 leading-relaxed">
        Recorded from the sharing as <strong>{recipient?.name || recipient?.email}</strong>
        {recipient?.isSender ? ', who shared the information' : ''}. Reg 3(5) needs an identifier
        before this person can appear in the register.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name" required>
          <TextField value={form.name} onChange={(e) => set('name')(e.target.value)} />
        </Field>
        <Field label="Category" required>
          <Select value={form.category} onChange={set('category')} options={INSIDER_CATEGORIES} />
        </Field>
        <Field label="PAN">
          <TextField
            value={form.pan}
            onChange={(e) => set('pan')(e.target.value.toUpperCase())}
            placeholder="ABCDE1234F"
            maxLength={10}
          />
        </Field>
        <Field label="Organisation">
          <TextField value={form.organisation} onChange={(e) => set('organisation')(e.target.value)} />
        </Field>
      </div>

      <div className="mt-4">
        <div className="text-[11px] text-[#7a7060] mb-2">
          No PAN? Reg 3(5) allows another identifier authorised by law — a passport or tax number
          for someone outside India, for instance.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Alternate identifier">
            <TextField
              value={form.alternateId}
              onChange={(e) => set('alternateId')(e.target.value)}
              placeholder="Passport or tax number"
            />
          </Field>
          <Field label="Type">
            <Select
              value={form.alternateIdType}
              onChange={set('alternateIdType')}
              options={[
                { value: 'passport', label: 'Passport' },
                { value: 'tax_id', label: 'Tax identification number' },
                { value: 'other', label: 'Other' },
              ]}
              disabled={!form.alternateId.trim()}
            />
          </Field>
        </div>
      </div>

      <div className="text-[11px] text-[#a09684] mt-4 leading-relaxed">
        The identifier is encrypted before it is stored, and only the masked form appears in the
        register. Adding someone here records nothing — you still press <strong>Record in
        register</strong> to seal the entry.
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  NOTICES — Reg 3(2B)                                       */
/* ═══════════════════════════════════════════════════════════ */

const NOTICE_TONES = {
  acknowledged: '#4a6b3f',
  sent: '#8a6a1f',
  pending: '#9a564a',
  failed: '#9a564a',
  not_required: '#a09684',
};

/**
 * Recording the sharing is only half the duty. Reg 3(2B) makes the recipient an
 * insider by operation of the regulation, and requires the company to tell them
 * so. This is where a compliance officer sees which of those notices are still
 * outstanding — the number they are personally accountable for.
 */
function NoticesTab({ companyId, can, showToast }) {
  const [notices, setNotices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [outstandingOnly, setOutstandingOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sddAPI.getNotices(companyId, outstandingOnly ? { outstanding: true } : {});
      setNotices(res?.data?.notices || []);
      setSummary(res?.data?.summary || null);
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, outstandingOnly, showToast]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, fn, successMessage) => {
    setBusy(id);
    try {
      const res = await fn();
      showToast(res?.message || successMessage, 'success');
      load();
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy(null);
    }
  };

  const acknowledge = (notice) => {
    const note = window.prompt(
      'How was the acknowledgement received? For example: "Signed confirmation received by post, 12 Aug 2026."',
    );
    if (!note || !note.trim()) return;
    act(notice._id, () => sddAPI.acknowledgeNotice(companyId, notice._id, note.trim()), 'Acknowledgement recorded.');
  };

  return (
    <>
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Notices issued', value: summary.total },
            { label: 'Acknowledged', value: summary.acknowledged },
            { label: 'Sent, awaiting reply', value: summary.sent },
            { label: 'Outstanding', value: summary.outstanding, alert: summary.outstanding > 0 },
          ].map((tile) => (
            <Card key={tile.label} className="!p-4">
              <div
                className={`text-[26px] font-bold leading-none tabular-nums ${tile.alert ? 'text-[#9a564a]' : ''}`}
              >
                {tile.value}
              </div>
              <div className="text-[12px] text-[#7a7060] mt-1.5">{tile.label}</div>
            </Card>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <p className="text-[12px] text-[#7a7060] max-w-2xl leading-relaxed">
          Reg 3(2B): anyone who receives UPSI for a legitimate purpose becomes an insider, and must
          be given due notice to keep it confidential. A notice is sent automatically when a sharing
          is recorded. Where a recipient has no email address, serve it another way and record the
          acknowledgement here.
        </p>
        <Button
          variant={outstandingOnly ? 'ink' : 'outline'}
          size="sm"
          onClick={() => setOutstandingOnly((v) => !v)}
        >
          {outstandingOnly ? 'Showing outstanding' : 'Show outstanding only'}
        </Button>
      </div>

      {loading ? (
        <Card><Empty>Loading…</Empty></Card>
      ) : notices.length === 0 ? (
        <Card>
          <Empty>
            {outstandingOnly
              ? 'Nothing outstanding — every notice has been served or acknowledged.'
              : 'No notices yet. One is issued for each recipient when a sharing is recorded.'}
          </Empty>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.6px] text-[#7a7060] border-b border-[#e8e2d6]">
                <th className="py-2.5 pr-3">Entry</th>
                <th className="py-2.5 pr-3">Recipient</th>
                <th className="py-2.5 pr-3">Nature of UPSI</th>
                <th className="py-2.5 pr-3">Status</th>
                <th className="py-2.5" />
              </tr>
            </thead>
            <tbody>
              {notices.map((notice) => (
                <tr key={notice._id} className="border-b border-[#f0ece2] align-top">
                  <td className="py-3 pr-3 font-mono text-[#a09684] whitespace-nowrap">
                    #{notice.shareSeq}
                  </td>
                  <td className="py-3 pr-3">
                    <div className="font-semibold">{notice.recipientName}</div>
                    <div className="text-[11px] text-[#a09684]">
                      {notice.recipientEmail || 'no email on record'}
                    </div>
                  </td>
                  <td className="py-3 pr-3">{notice.natureOfUpsi}</td>
                  <td className="py-3 pr-3">
                    <Chip tone={NOTICE_TONES[notice.status] || '#a09684'}>
                      {String(notice.status).replace(/_/g, ' ')}
                    </Chip>
                    {notice.acknowledgedAt && (
                      <div className="text-[11px] text-[#a09684] mt-1">
                        {fmtDateTime(notice.acknowledgedAt)}
                        {notice.acknowledgedVia === 'manual' ? ' · recorded manually' : ' · by link'}
                      </div>
                    )}
                    {notice.status === 'failed' && notice.failureReason && (
                      <div className="text-[11px] text-[#9a564a] mt-1 max-w-[260px]">
                        {notice.failureReason}
                      </div>
                    )}
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    {can.recordSharing && !notice.acknowledgedAt && (
                      <>
                        {notice.recipientEmail && (
                          <Button
                            variant="ghost"
                            size="xs"
                            loading={busy === notice._id}
                            onClick={() => act(
                              notice._id,
                              () => sddAPI.resendNotice(companyId, notice._id),
                              'Notice sent again.',
                            )}
                          >
                            Resend
                          </Button>
                        )}
                        <Button variant="ghost" size="xs" onClick={() => acknowledge(notice)}>
                          Mark acknowledged
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  REGISTER                                                  */
/* ═══════════════════════════════════════════════════════════ */

function RegisterTab({ companyId, rows, insiders, events, supersededBy, can, onChanged, showToast }) {
  const [showForm, setShowForm] = useState(false);
  const [correcting, setCorrecting] = useState(null);

  return (
    <>
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <div className="text-[13px] text-[#7a7060]">
          {rows.length === 0 ? 'No sharings recorded yet.' : `${rows.length} sharing(s) recorded.`}
        </div>
        {can.recordSharing && (
          <Button
            size="sm"
            leftIcon={<TbPlus />}
            onClick={() => { setCorrecting(null); setShowForm(true); }}
            disabled={insiders.length < 1 || events.length < 1}
            title={insiders.length < 1 || events.length < 1 ? 'Add an insider and a UPSI event first' : undefined}
          >
            Record a sharing
          </Button>
        )}
      </div>

      {(showForm || correcting) && (
        <ShareForm
          companyId={companyId}
          insiders={insiders}
          events={events}
          correcting={correcting}
          onClose={() => { setShowForm(false); setCorrecting(null); }}
          onSaved={() => { setShowForm(false); setCorrecting(null); onChanged(); }}
          showToast={showToast}
        />
      )}

      {rows.length === 0 ? (
        <Card><Empty>Nothing here yet. Record the first sharing of UPSI.</Empty></Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.6px] text-[#7a7060] border-b border-[#e8e2d6]">
                <th className="py-2.5 pr-3">#</th>
                <th className="py-2.5 pr-3">Shared at</th>
                <th className="py-2.5 pr-3">Nature of UPSI</th>
                <th className="py-2.5 pr-3">By</th>
                <th className="py-2.5 pr-3">Recipients</th>
                <th className="py-2.5 pr-3">Purpose</th>
                <th className="py-2.5 pr-3">Mode</th>
                <th className="py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const supersededSeq = supersededBy.get(String(row._id));
                return (
                  <tr
                    key={row._id}
                    className={`border-b border-[#f0ece2] align-top ${supersededSeq ? 'opacity-60' : ''}`}
                  >
                    <td className="py-3 pr-3 font-semibold whitespace-nowrap">
                      {row.seq}
                      {row.correctionOf && (
                        <Chip tone="#8a6a1f" className="ml-1.5">correction</Chip>
                      )}
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap">{fmtDateTime(row.sharedAt)}</td>
                    <td className={`py-3 pr-3 ${supersededSeq ? 'line-through' : ''}`}>
                      {row.natureOfUpsi}
                      {supersededSeq && (
                        <div className="text-[11px] text-[#9a564a] no-underline mt-0.5">
                          Superseded by row {supersededSeq}
                        </div>
                      )}
                      {/* The matter is the Reg 3(5) particular; the document is
                          what someone would have to produce to evidence it.
                          Grouped here rather than given a column of its own —
                          both answer "what was shared". */}
                      {row.sourceDocument?.title && (
                        <div className="text-[11px] text-[#7a7060] mt-1 no-underline">
                          <span className="text-[#a09684]">via </span>
                          {row.sourceDocument.title}
                          {row.sourceDocument.ref && (
                            <span className="font-mono text-[#a09684]"> {row.sourceDocument.ref}</span>
                          )}
                          {row.sourceDocument.type && ` · ${docTypeLabel(row.sourceDocument.type)}`}
                        </div>
                      )}
                      {row.correctionReason && (
                        <div className="text-[11px] text-[#7a7060] mt-0.5">{row.correctionReason}</div>
                      )}
                    </td>
                    <td className="py-3 pr-3">{row.sharedByName}</td>
                    <td className="py-3 pr-3">
                      {(row.recipients || []).map((r, i) => (
                        <div key={i} className="whitespace-nowrap">
                          {r.name}
                          {r.organisation ? ` · ${r.organisation}` : ''}
                          {r.panMasked && <span className="text-[#a09684]"> [{r.panMasked}]</span>}
                        </div>
                      ))}
                    </td>
                    <td className="py-3 pr-3 max-w-[240px]">{row.purpose}</td>
                    <td className="py-3 pr-3">{labelOf(SHARE_MODES, row.mode)}</td>
                    <td className="py-3 text-right whitespace-nowrap">
                      {/* No Edit. No Delete. Correction is the only way to change
                          what the register says. */}
                      {!supersededSeq && can.correctSharing && (
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => { setShowForm(false); setCorrecting(row); }}
                        >
                          Correct
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ShareForm({ companyId, insiders, events, correcting, onClose, onSaved, showToast }) {
  // Date and time are separate inputs but ONE legal field. Reg 3(5) names time
  // stamping, and an insider-trading inquiry turns on whether a sharing came
  // before or after a trade — "sometime on 11 August" does not settle that.
  const [form, setForm] = useState(() => {
    const source = correcting?.sharedAt ? new Date(correcting.sharedAt) : new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return {
      upsiEvent: correcting?.upsiEvent || '',
      sharedByInsider: correcting?.sharedByInsider || '',
      recipients: correcting ? (correcting.recipients || []).map((r) => String(r.insider)) : [],
      purpose: correcting?.purpose || '',
      mode: correcting?.mode || 'email',
      // Local date/time, not UTC: a compliance officer records the wall-clock
      // time they shared it. toISOString() would shift it by the offset.
      sharedAtDate: `${source.getFullYear()}-${pad(source.getMonth() + 1)}-${pad(source.getDate())}`,
      sharedAtTime: `${pad(source.getHours())}:${pad(source.getMinutes())}`,
      correctionReason: '',
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleRecipient = (id) =>
    setForm((f) => ({
      ...f,
      recipients: f.recipients.includes(id)
        ? f.recipients.filter((r) => r !== id)
        : [...f.recipients, id],
    }));

  const submit = async () => {
    setSaving(true);
    try {
      const { sharedAtDate, sharedAtTime, ...rest } = form;
      // Recombine into the single instant the register stores. Missing time is
      // treated as 00:00 rather than refused — but the field is required below,
      // so that only happens if a browser hands back an empty time input.
      const sharedAt = sharedAtDate
        ? new Date(`${sharedAtDate}T${sharedAtTime || '00:00'}`).toISOString()
        : undefined;
      const body = { ...rest, sharedAt };
      const res = correcting
        ? await sddAPI.correctShare(companyId, correcting._id, body)
        : await sddAPI.recordShare(companyId, body);

      showToast(res?.message || 'Recorded.', 'success');
      // Warnings are advisory — the row is already sealed in the chain. Surface
      // them so the compliance officer sees them, but never block the write.
      for (const warning of res?.data?.warnings || []) showToast(warning, 'warning', 6000);
      onSaved();
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-5">
      <div className="text-[15px] font-bold mb-1">
        {correcting ? `Correct row ${correcting.seq}` : 'Record a sharing of UPSI'}
      </div>
      <div className="text-[12px] text-[#7a7060] mb-4">
        {correcting
          ? 'This writes a NEW row. Row ' + correcting.seq + ' stays in the register, marked as superseded.'
          : 'Once saved this row is sealed into the hash chain and cannot be edited.'}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="UPSI event" required>
          <Select
            value={form.upsiEvent}
            onChange={set('upsiEvent')}
            options={events.map((e) => ({
              value: e._id,
              label: `${e.title}${e.status === 'published' ? ' (published)' : ''}`,
            }))}
          />
        </Field>

        <Field label="Shared by" required>
          <Select
            value={form.sharedByInsider}
            onChange={set('sharedByInsider')}
            options={insiders.map((i) => ({ value: i._id, label: i.name }))}
          />
        </Field>

        <Field label="Date of sharing" required>
          <DateField value={form.sharedAtDate} onChange={set('sharedAtDate')} />
        </Field>

        <Field label="Time of sharing" required>
          <input
            type="time"
            className={inputClass}
            value={form.sharedAtTime}
            onChange={(e) => set('sharedAtTime')(e.target.value)}
          />
        </Field>

        <Field label="How it was shared" required>
          <Select value={form.mode} onChange={set('mode')} options={SHARE_MODES} />
        </Field>
      </div>
      <div className="text-[11px] text-[#7a7060] mt-2">
        Record the time as accurately as you can. Reg 3(5) requires time stamping, and whether a
        sharing came before or after a trade is the question in an inquiry.
      </div>

      <div className="mt-4">
        <span className={labelClass}>Recipients *</span>
        <div className="flex flex-wrap gap-2 mt-1">
          {insiders.length === 0 && (
            <span className="text-[12px] text-[#7a7060]">No active insiders. Add one first.</span>
          )}
          {insiders.map((i) => {
            const selected = form.recipients.includes(i._id);
            return (
              <button
                key={i._id}
                type="button"
                onClick={() => toggleRecipient(i._id)}
                className={`px-3 py-1.5 rounded-full text-[12px] border transition ${
                  selected
                    ? 'bg-[#8a6a1f] text-white border-[#8a6a1f]'
                    : 'bg-white text-[#4a4034] border-[#e8e2d6] hover:border-[#c8bfae]'
                }`}
              >
                {i.name}
                {i.panMasked ? ` · ${i.panMasked}` : ''}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <span className={labelClass}>Legitimate purpose * <span className="normal-case font-normal">(Reg 3(2A))</span></span>
        <textarea
          className={textareaClass}
          rows={2}
          value={form.purpose}
          onChange={(e) => set('purpose')(e.target.value)}
          placeholder="Why was this information shared? e.g. Statutory audit of quarterly results"
        />
      </div>

      {correcting && (
        <div className="mt-4">
          <span className={labelClass}>Why was the original wrong? *</span>
          <input
            className={inputClass}
            value={form.correctionReason}
            onChange={(e) => set('correctionReason')(e.target.value)}
            placeholder="e.g. Recipient recorded in error"
          />
        </div>
      )}

      <div className="flex gap-2 mt-5">
        <Button
          onClick={submit}
          loading={saving}
          disabled={!form.upsiEvent || !form.sharedByInsider || !form.recipients.length || !form.purpose.trim() || !form.sharedAtDate || !form.sharedAtTime || (correcting && !form.correctionReason.trim())}
        >
          {correcting ? 'Record correction' : 'Record in register'}
        </Button>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  INSIDERS                                                  */
/* ═══════════════════════════════════════════════════════════ */

function InsidersTab({ companyId, insiders, can, onChanged, showToast }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'designated_person', pan: '', alternateId: '', organisation: '', designation: '', email: '', isExternal: false });
  const [saving, setSaving] = useState(false);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    setSaving(true);
    try {
      await sddAPI.createInsider(companyId, {
        ...form,
        isExternal: form.category.startsWith('external_'),
      });
      showToast('Insider added.', 'success');
      setForm({ name: '', category: 'designated_person', pan: '', alternateId: '', organisation: '', designation: '', email: '', isExternal: false });
      setShowForm(false);
      onChanged();
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const standDown = async (insider) => {
    try {
      await sddAPI.deactivateInsider(companyId, insider._id);
      showToast(`${insider.name} stood down. Existing register rows are unaffected.`, 'success');
      onChanged();
    } catch (error) {
      showToast(errText(error), 'error');
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <div className="text-[13px] text-[#7a7060]">
          {insiders.length} person(s) on the list.
        </div>
        {can.manageInsiders && (
          <Button size="sm" leftIcon={<TbPlus />} onClick={() => setShowForm((s) => !s)}>
            Add insider
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name" required>
              <TextField value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder="Full name" />
            </Field>
            <Field label="Category" required>
              <Select value={form.category} onChange={set('category')} options={INSIDER_CATEGORIES} />
            </Field>
            <Field label="PAN">
              <TextField
                value={form.pan}
                onChange={(e) => set('pan')(e.target.value.toUpperCase())}
                placeholder="ABCDE1234F"
                maxLength={10}
              />
            </Field>
            <Field label="Alternate ID (where no PAN)">
              <TextField value={form.alternateId} onChange={(e) => set('alternateId')(e.target.value)} placeholder="Passport / tax ID" />
            </Field>
            <Field label="Organisation">
              <TextField value={form.organisation} onChange={(e) => set('organisation')(e.target.value)} />
            </Field>
            <Field label="Email">
              <TextField value={form.email} onChange={(e) => set('email')(e.target.value)} type="email" />
            </Field>
          </div>
          <div className="text-[11px] text-[#7a7060] mt-3">
            The PAN is encrypted before it is stored. Only the masked form (ABCDE****F) is shown
            here or written into the register.
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={submit} loading={saving} disabled={!form.name || (!form.pan && !form.alternateId)}>
              Add
            </Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {insiders.length === 0 ? (
        <Card><Empty>No insiders yet. Add the directors, KMP and external parties who handle UPSI.</Empty></Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.6px] text-[#7a7060] border-b border-[#e8e2d6]">
                <th className="py-2.5 pr-3">Name</th>
                <th className="py-2.5 pr-3">Category</th>
                <th className="py-2.5 pr-3">Identifier</th>
                <th className="py-2.5 pr-3">Organisation</th>
                <th className="py-2.5 pr-3">Status</th>
                <th className="py-2.5" />
              </tr>
            </thead>
            <tbody>
              {insiders.map((i) => (
                <tr key={i._id} className="border-b border-[#f0ece2]">
                  <td className="py-3 pr-3 font-semibold">{i.name}</td>
                  <td className="py-3 pr-3">{labelOf(INSIDER_CATEGORIES, i.category)}</td>
                  <td className="py-3 pr-3 text-[#7a7060]">{i.panMasked || i.alternateIdMasked || '—'}</td>
                  <td className="py-3 pr-3">{i.organisation || '—'}</td>
                  <td className="py-3 pr-3">
                    {i.effectiveTo
                      ? <Chip tone="#9a564a">Stood down</Chip>
                      : <Chip tone="#4a6b3f">Active</Chip>}
                  </td>
                  <td className="py-3 text-right">
                    {/* Never a delete — historical rows must keep naming them. */}
                    {!i.effectiveTo && can.manageInsiders && (
                      <Button variant="ghost" size="xs" onClick={() => standDown(i)}>Stand down</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  UPSI EVENTS                                               */
/* ═══════════════════════════════════════════════════════════ */

const emptyEventForm = { title: '', category: 'financial_results', description: '', standingPurpose: '' };

function EventsTab({ companyId, events, can, onChanged, showToast }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyEventForm);
  const [saving, setSaving] = useState(false);
  // Inline editor for one matter's standing purpose — the only field a matter
  // needs after creation, and the one that decides whether its email seals.
  const [editingPurpose, setEditingPurpose] = useState(null);
  const [purposeDraft, setPurposeDraft] = useState('');

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    setSaving(true);
    try {
      const res = await sddAPI.createEvent(companyId, form);
      const code = res?.data?.event?.code;
      showToast(code ? `UPSI event created as UPSI-${code}. Insiders put [UPSI-${code}] in the email subject.` : 'UPSI event created.', 'success');
      setForm(emptyEventForm);
      setShowForm(false);
      onChanged();
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const savePurpose = async (event) => {
    setSaving(true);
    try {
      await sddAPI.updateEvent(companyId, event._id, { standingPurpose: purposeDraft });
      showToast(purposeDraft.trim() ? 'Standing purpose saved. Email for this matter will now record itself.' : 'Standing purpose cleared. Email for this matter will wait for a person.', 'success');
      setEditingPurpose(null);
      onChanged();
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const publish = async (event) => {
    // One-way and irreversible: it fixes the moment the information stopped
    // being UPSI, which is the pivot of any later insider-trading question.
    const ok = window.confirm(
      `Record "${event.title}" as published?\n\nThis fixes the moment the information stopped being UPSI. It cannot be undone or changed afterwards.`,
    );
    if (!ok) return;
    try {
      await sddAPI.publishEvent(companyId, event._id, { publishedAt: new Date().toISOString() });
      showToast('Publication recorded. This is now fixed.', 'success');
      onChanged();
    } catch (error) {
      showToast(errText(error), 'error');
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <div className="text-[13px] text-[#7a7060]">{events.length} event(s).</div>
        {can.manageEvents && (
          <Button size="sm" leftIcon={<TbPlus />} onClick={() => setShowForm((s) => !s)}>Add UPSI event</Button>
        )}
      </div>

      {showForm && (
        <Card className="mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title" required>
              <TextField value={form.title} onChange={(e) => set('title')(e.target.value)} placeholder="e.g. Q3 FY26 unaudited results" />
            </Field>
            <Field label="Category" required>
              <Select value={form.category} onChange={set('category')} options={UPSI_CATEGORIES} />
            </Field>
          </div>
          <div className="mt-4">
            <span className={labelClass}>Description</span>
            <textarea className={textareaClass} rows={2} value={form.description} onChange={(e) => set('description')(e.target.value)} />
          </div>
          <div className="mt-4">
            <span className={labelClass}>Standing purpose for email entries (Reg 3(2A))</span>
            <TextField value={form.standingPurpose} onChange={(e) => set('standingPurpose')(e.target.value)} placeholder="e.g. Statutory audit of Q2 FY27 results" />
            <div className="text-[11px] text-[#7a7060] mt-1.5 leading-relaxed">
              When an insider emails this matter with its code in the subject and copies the UPSI mailbox, the
              entry is sealed automatically with this purpose. Leave blank and such emails wait for a person to state one.
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={submit} loading={saving} disabled={!form.title}>Create</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {events.length === 0 ? (
        <Card><Empty>No UPSI events yet. Create one before recording a sharing.</Empty></Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.6px] text-[#7a7060] border-b border-[#e8e2d6]">
                <th className="py-2.5 pr-3">Code</th>
                <th className="py-2.5 pr-3">Title</th>
                <th className="py-2.5 pr-3">Category</th>
                <th className="py-2.5 pr-3">Status</th>
                <th className="py-2.5 pr-3">Email entries</th>
                <th className="py-2.5 pr-3">Published at</th>
                <th className="py-2.5" />
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e._id} className="border-b border-[#f0ece2] align-top">
                  <td className="py-3 pr-3 font-mono text-[12px] whitespace-nowrap">{e.code != null ? `UPSI-${e.code}` : '—'}</td>
                  <td className="py-3 pr-3 font-semibold">{e.title}</td>
                  <td className="py-3 pr-3">{labelOf(UPSI_CATEGORIES, e.category)}</td>
                  <td className="py-3 pr-3">
                    {e.status === 'published'
                      ? <Chip tone="#4a6b3f">Published</Chip>
                      : <Chip tone="#8a6a1f">Unpublished — still UPSI</Chip>}
                  </td>
                  <td className="py-3 pr-3 max-w-xs">
                    {editingPurpose === e._id ? (
                      <div className="space-y-2">
                        <TextField value={purposeDraft} onChange={(ev) => setPurposeDraft(ev.target.value)} placeholder="e.g. Statutory audit of Q2 results" autoFocus />
                        <div className="flex gap-2">
                          <Button size="xs" onClick={() => savePurpose(e)} loading={saving}>Save</Button>
                          <Button size="xs" variant="ghost" onClick={() => setEditingPurpose(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : e.standingPurpose ? (
                      <div>
                        <div className="text-[12px] text-[#4a6b3f]">Seal automatically</div>
                        <div className="text-[11px] text-[#7a7060] leading-snug">Purpose: {e.standingPurpose}</div>
                        {can.manageEvents && (
                          <button type="button" className="text-[11px] text-[#8a6a1f] underline mt-1" onClick={() => { setEditingPurpose(e._id); setPurposeDraft(e.standingPurpose); }}>Change</button>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="text-[12px] text-[#a09684]">Wait for a person</div>
                        {can.manageEvents && (
                          <button type="button" className="text-[11px] text-[#8a6a1f] underline mt-1" onClick={() => { setEditingPurpose(e._id); setPurposeDraft(''); }}>Set standing purpose</button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3 pr-3 whitespace-nowrap">{e.publishedAt ? fmtDateTime(e.publishedAt) : '—'}</td>
                  <td className="py-3 text-right">
                    {/* Publication is irreversible, so it sits above the daily
                        work — Compliance Officer only. */}
                    {e.status !== 'published' && can.publishEvent && (
                      <Button variant="outline" size="xs" onClick={() => publish(e)}>Mark published</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  INTEGRITY                                                 */
/* ═══════════════════════════════════════════════════════════ */

function IntegrityTab({ integrity, retention, rowCount, can, onVerify, onPlaceHold, onReleaseHold, loading }) {
  return (
    <>
      {!integrity ? (
        <Card>
          <div className="text-center py-8">
            <TbShieldCheck size={40} className="mx-auto mb-3 text-[#a09684]" />
            <div className="text-[14px] font-semibold mb-1">Verify the register</div>
            <p className="text-[12px] text-[#7a7060] max-w-md mx-auto mb-4">
              Recomputes the hash of every one of the {rowCount} row(s) and checks each link in the
              chain. If any row has been altered, removed or replaced — including directly in the
              database — this names it.
            </p>
            <Button onClick={onVerify} loading={loading} leftIcon={<TbShieldCheck />}>Run verification</Button>
          </div>
        </Card>
      ) : (
        <IntegrityResult integrity={integrity} onVerify={onVerify} loading={loading} />
      )}

      <RetentionPanel
        retention={retention}
        canManageHold={!!can.manageLegalHold}
        onPlaceHold={onPlaceHold}
        onReleaseHold={onReleaseHold}
      />
    </>
  );
}

function IntegrityResult({ integrity, onVerify, loading }) {
  return (
    <>
      <Card className={integrity.ok ? 'border-[#4a6b3f]' : 'border-[#9a564a]'}>
        <div className="flex items-start gap-3">
          {integrity.ok
            ? <TbShieldCheck size={28} className="text-[#4a6b3f] shrink-0" />
            : <TbShieldX size={28} className="text-[#9a564a] shrink-0" />}
          <div className="min-w-0">
            <div className={`text-[15px] font-bold ${integrity.ok ? 'text-[#4a6b3f]' : 'text-[#9a564a]'}`}>
              {integrity.ok ? 'Register intact' : 'Register has been altered'}
            </div>
            <div className="text-[12px] text-[#7a7060] mt-0.5">{integrity.message}</div>
          </div>
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={onVerify} loading={loading}>Re-run</Button>
          </div>
        </div>
      </Card>

      {!integrity.ok && (
        <Card className="mt-4">
          <div className="flex items-center gap-2 mb-3 text-[13px] font-bold text-[#9a564a]">
            <TbAlertTriangle size={16} /> Failures
          </div>
          <div className="space-y-3">
            {(integrity.failures || []).map((f, i) => (
              <div key={i} className="border-l-2 border-[#9a564a] pl-3">
                <div className="text-[13px] font-semibold">
                  Row {f.seq} · <span className="text-[#9a564a]">{String(f.type).replace(/_/g, ' ')}</span>
                </div>
                <div className="text-[12px] text-[#7a7060]">{f.message}</div>
                {/* expected/actual are hashes for content and chain failures, but
                    row POSITIONS for a sequence gap — render both as text. */}
                <div className="text-[11px] text-[#a09684] font-mono mt-1 break-all">
                  expected {String(f.expected ?? '—')} · actual {String(f.actual ?? '—')}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

/* ─── Retention & legal hold — Reg 3(6) ─────────────────────── */

function RetentionPanel({ retention, canManageHold, onPlaceHold, onReleaseHold }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ reference: '', reason: '' });

  if (!retention) return null;

  const held = retention.heldByLaw;

  return (
    <Card className="mt-4">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold mb-0.5">Retention — {retention.retentionYears} years</div>
          <div className="text-[12px] text-[#7a7060]">{retention.summary}</div>

          {retention.rowCount > 0 && !held && (
            <div className="text-[11px] text-[#a09684] mt-2">
              Earliest entry {fmtDateTime(retention.earliestEntry)} · first eligible for disposal{' '}
              {fmtDateTime(retention.firstEligibleAt)}
            </div>
          )}
        </div>

        {held
          ? <Chip tone="#9a564a">Held — preserved indefinitely</Chip>
          : <Chip tone="#4a6b3f">No hold</Chip>}
      </div>

      {(retention.openHolds || []).map((hold) => (
        <div key={hold.id} className="mt-3 border-l-2 border-[#9a564a] pl-3">
          <div className="text-[12px] font-semibold">{hold.reference}</div>
          <div className="text-[12px] text-[#7a7060]">{hold.reason}</div>
          <div className="text-[11px] text-[#a09684] mt-0.5">
            Received {fmtDateTime(hold.receivedAt)}
            {hold.placedByName ? ` · placed by ${hold.placedByName}` : ''}
          </div>
          {canManageHold && (
            <Button
              variant="ghost"
              size="xs"
              className="mt-1"
              onClick={() => {
                const reason = window.prompt(
                  'Why is the hold being released? Normally: the proceedings have concluded.',
                );
                if (reason && reason.trim()) onReleaseHold(hold.id, reason.trim());
              }}
            >
              Release hold
            </Button>
          )}
        </div>
      ))}

      {showForm ? (
        <div className="mt-4 pt-4 border-t border-[#f0ece2]">
          <div className="text-[12px] text-[#7a7060] mb-3">
            Reg 3(6): where SEBI communicates an investigation or enforcement proceeding, the
            register must be preserved until those proceedings conclude — past the{' '}
            {retention.retentionYears}-year period.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="SEBI reference" required>
              <TextField
                value={form.reference}
                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                placeholder="e.g. SEBI/HO/ISD/2026/1234"
              />
            </Field>
            <Field label="Reason" required>
              <TextField
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Investigation into trading in Q3 results"
              />
            </Field>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              size="sm"
              disabled={!form.reference.trim() || !form.reason.trim()}
              onClick={() => {
                onPlaceHold(form);
                setForm({ reference: '', reason: '' });
                setShowForm(false);
              }}
            >
              Place hold
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      ) : canManageHold ? (
        <Button variant="outline" size="xs" className="mt-3" onClick={() => setShowForm(true)}>
          Place a legal hold
        </Button>
      ) : null}
    </Card>
  );
}
